import React, { useState, useMemo, useEffect } from 'react';
import { useQCM } from '../../contexts/QCMContext';
import { useTranslation } from 'react-i18next';
import QCMOptionToggle from './QCMOptionToggle';
import ExamQCMOptions from './ExamQCMOptions';

const QCMOptions = ({ stepData }) => {
    const { t } = useTranslation();
    const {
        currentQCM,
        selectedOption,
        setSelectedOption,
        degreeOfCertainty,
        setDegreeOfCertainty,
        submitQCMAnswer,
        loading,
        setHoveredCertainty,
        isLastStep,
        goToNextFunnelStep,
        setDisplayMode,
        funnelUserChoices,
        setFunnelUserChoices,
        setLastFunnelResult,
        setShowSummary,
        setShowKnowledgeReinforcement,
    } = useQCM();

    const [hoveredOption, setHoveredOption] = useState(null);
    const [markedKOAnswers, setMarkedKOAnswers] = useState(new Set()); // Answers marked via KO certainty bars
    const [selectedAnswers, setSelectedAnswers] = useState(new Set()); // Answers selected via certainty bars

    // Reset local states when currentQCM changes
    React.useEffect(() => {
        setMarkedKOAnswers(new Set());
        setSelectedAnswers(new Set());
    }, [currentQCM?.qcm_id, stepData?.qcm_id]);

    // Support pour les données d'étapes funnel QCM ou QCM normaux
    // Gérer deux formats: stepData.qcm ou stepData directement
    const qcmSource = React.useMemo(() => {
        if (stepData) {
            if (stepData.qcm) return stepData.qcm;
            if (stepData.correct_answers || stepData.correct_answer || stepData.distractors || stepData.question) return stepData;
        }
        if (currentQCM?.qcm) return currentQCM.qcm;
        if (currentQCM && (currentQCM.correct_answers || currentQCM.correct_answer || currentQCM.distractors || currentQCM.question)) return currentQCM;
        return null;
    }, [stepData, currentQCM]);

    // Mélanger aléatoirement les options pour éviter de révéler la bonne réponse
    const allOptions = useMemo(() => {
        if (!qcmSource) return [];
        const q = qcmSource;
        const normalize = (opt) => {
            if (!opt) return null;
            if (typeof opt === 'string') return { answer: opt };
            if (opt && typeof opt.answer === 'string') return opt;
            return null;
        };
        const correctFromSingular = Array.isArray(q.correct_answer)
            ? q.correct_answer.map(normalize)
            : (q.correct_answer ? [normalize(q.correct_answer)] : []);
        const correctFromPlural = Array.isArray(q.correct_answers)
            ? q.correct_answers.map(normalize)
            : [];
        const distractors = Array.isArray(q.distractors) ? q.distractors.map(normalize) : [];
        const options = [...correctFromSingular, ...correctFromPlural, ...distractors].filter(Boolean);
        
        // Mélange Fisher-Yates pour un mélange vraiment aléatoire
        const shuffledOptions = [...options];
        for (let i = shuffledOptions.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffledOptions[i], shuffledOptions[j]] = [shuffledOptions[j], shuffledOptions[i]];
        }
        
        console.log('🎲 Options mélangées:', shuffledOptions.map(opt => opt.answer));
        return shuffledOptions;
    }, [qcmSource?.correct_answer, qcmSource?.correct_answers, qcmSource?.distractors]);

    const correctAnswerTexts = useMemo(() => {
        const q = qcmSource;
        if (!q) return new Set();
        const texts = [];
        if (Array.isArray(q.correct_answer)) {
            for (const c of q.correct_answer) {
                if (typeof c === 'string') texts.push(c);
                else if (c && typeof c.answer === 'string') texts.push(c.answer);
            }
        } else if (q.correct_answer && typeof q.correct_answer.answer === 'string') {
            texts.push(q.correct_answer.answer);
        }
        if (Array.isArray(q.correct_answers)) {
            for (const c of q.correct_answers) {
                if (typeof c === 'string') texts.push(c);
                else if (c && typeof c.answer === 'string') texts.push(c.answer);
            }
        }
        return new Set(texts.filter(Boolean));
    }, [qcmSource?.correct_answer, qcmSource?.correct_answers]);

    const distractorAnswerTexts = useMemo(() => {
        const q = qcmSource;
        if (!q) return new Set();
        const texts = [];
        if (Array.isArray(q.distractors)) {
            for (const d of q.distractors) {
                if (typeof d === 'string') texts.push(d);
                else if (d && typeof d.answer === 'string') texts.push(d.answer);
            }
        }
        return new Set(texts.filter(Boolean));
    }, [qcmSource?.distractors]);

    const checkCompletion = useMemo(() => {
        return () => {
            // Completion only for funnel (stepData present)
            if (!stepData) return;
            // Advance when every unique option is classified (OK or KO)
            const uniqueAnswers = Array.from(new Set(allOptions.map(o => o.answer)));
            const userChosen = new Set([...Array.from(selectedAnswers), ...Array.from(markedKOAnswers)]);
            if (userChosen.size !== uniqueAnswers.length) return;

            // Build result JSON for Solution view
            const correctSet = correctAnswerTexts;
            const incorrectSet = new Set(uniqueAnswers.filter(a => !correctSet.has(a)));
            const userChoices = {};
            for (const a of uniqueAnswers) {
                const c = funnelUserChoices[a];
                if (c && (c.choice === 'OK' || c.choice === 'KO')) {
                    userChoices[a] = { choice: c.choice, confidence: c.confidence || '' };
                }
            }
            const correctUser = uniqueAnswers.filter(a => (correctSet.has(a) && userChoices[a]?.choice === 'OK') || (incorrectSet.has(a) && userChoices[a]?.choice === 'KO'));
            const incorrectUser = uniqueAnswers.filter(a => !correctUser.includes(a));
            const score = correctUser.length;
            const total = uniqueAnswers.length;
            const result = {
                user: (currentQCM?.user || 'learner1'),
                date: new Date().toISOString(),
                step: currentQCM?.current_step || 1,
                qcm_id: currentQCM?.qcm_id || currentQCM?._id,
                correct: correctUser,
                incorrect: incorrectUser,
                choices: userChoices,
                score,
                total
            };
            setLastFunnelResult(result);

            // Move to solution view
            setTimeout(() => {
                setDisplayMode('solution');
            }, 0);
        };
    }, [stepData, selectedAnswers, markedKOAnswers, allOptions.length, setDisplayMode, correctAnswerTexts, currentQCM, funnelUserChoices, degreeOfCertainty, setLastFunnelResult]);

    // Immediate auto-advance when all classifications are correct (no extra click)
    useEffect(() => {
        if (!stepData) return;
        checkCompletion();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAnswers, markedKOAnswers, stepData, correctAnswerTexts, distractorAnswerTexts]);

    // Router: Use Exam style for regular QCMs, Funnel style for funnel QCMs
    if (!stepData && currentQCM?.questionType !== 'funnel-qcm') {
        return <ExamQCMOptions />;
    }

    // No early return; if qcmSource is missing or options empty, we fall back to an informative message below

    const handleOptionSelect = (option, certainty) => {
        // Convertir le libellé en ID technique pour l'API
        const certaintyMap = {'low': 1, 'medium': 2, 'high': 3};
        const certaintyId = certaintyMap[certainty] || 2;

        if (stepData) {
            // Funnel mode: classify this option as OK with a confidence level (exclusive with KO)
            setMarkedKOAnswers(prev => {
                const next = new Set(Array.from(prev));
                next.delete(option.answer);
                return next;
            });
            setSelectedAnswers(prev => {
                const next = new Set(Array.from(prev));
                next.add(option.answer); // always select as OK (do not toggle off)
                return next;
            });
            setFunnelUserChoices(prev => ({
                ...prev,
                [option.answer]: { choice: 'OK', confidence: certainty }
            }));
            setTimeout(() => checkCompletion(), 0);
        } else {
            // Standard mode: maintain legacy single-select behaviour
            setSelectedOption(option.answer);
            setDegreeOfCertainty(certainty);
            setTimeout(() => {
                submitQCMAnswer(option.answer, certaintyId);
            }, 100);
        }
    };

    const getCertaintyLabel = (level) => {
        switch (level) {
            case 'low': return t('low_certainty');
            case 'medium': return t('medium_certainty');
            case 'high': return t('high_certainty');
            default: return '';
        }
    };

    const getCertaintyColor = (level) => {
        switch (level) {
            case 'low': return 'text-red-400 bg-red-900/20 border-red-500/30';
            case 'medium': return 'text-yellow-400 bg-yellow-900/20 border-yellow-500/30';
            case 'high': return 'text-green-400 bg-green-900/20 border-green-500/30';
            default: return 'text-gray-400 bg-gray-700 border-gray-600';
        }
    };

    return (
        <div className="space-y-4">
            {/* Question Reminder (hidden in funnel/step mode) */}
            {!stepData && (
                <div className="bg-gray-700 rounded-lg p-4 mb-6">
                    <p className="text-gray-200 font-medium">
                        {qcmSource?.question || currentQCM?.question}
                    </p>
                </div>
            )}

            {/* Options */}
            <div className="space-y-4">
                {allOptions.length === 0 && (
                    <div className="text-center py-8">
                        <p className="text-gray-400">{t('no_options_available')}</p>
                    </div>
                )}
                {allOptions.map((option, index) => {
                    const isCorrect = correctAnswerTexts.has(option.answer);
                    const isSelected = selectedAnswers.has(option.answer);
                    const isMarkedDistractor = markedKOAnswers.has(option.answer);

                    // Disable card-click toggling in funnel mode to enforce exclusive controls (certainty vs KO)
                    const handleCardClick = () => {};

                    // Style per selection (funnel mode: no correctness highlighting)
                    let cardClass;
                    if (stepData) {
                        cardClass = 'border-gray-600 hover:border-gray-500';
                    } else {
                        cardClass = (selectedOption === option.answer && !isCorrect) ? 'border-red-500 bg-red-900/20' : 'border-gray-600 hover:border-gray-500';
                    }

                    return (
                        <div
                            key={index}
                            className={`bg-gray-700 rounded-lg border transition-colors relative ${cardClass}`}
                            onMouseEnter={() => setHoveredOption(index)}
                            onMouseLeave={() => setHoveredOption(null)}
                            onClick={handleCardClick}
                        >
                        {/* KO Toggle removed; replace with KO certainty bars below */}
                        
                        {/* Option Text */}
                        <div className="p-4 pr-12">
                            <div className="flex items-start">
                                <p className="text-gray-200 leading-relaxed flex-grow">
                                    {option.answer}
                                </p>
                            </div>
                        </div>

                        {/* Certainty Levels - OK (top) and KO (bottom) with labels */}
                        <div className="p-3 space-y-2">
                            {/* OK row */}
                            <div className="flex items-center space-x-2">
                                <span className="text-xs text-green-300 min-w-[28px]">OK</span>
                                <button
                                    onClick={(e) => { e.stopPropagation(); handleOptionSelect(option, 'low'); }}
                                    disabled={loading}
                                    className={`flex-1 h-2 bg-gray-600 rounded-full transition-all duration-200 hover:bg-red-500 ${stepData ? (funnelUserChoices[option.answer]?.choice === 'OK' && funnelUserChoices[option.answer]?.confidence === 'low' ? 'bg-red-500' : '') : (selectedOption === option.answer && degreeOfCertainty === 'low' ? 'bg-red-500' : '')} disabled:opacity-50 disabled:cursor-not-allowed`}
                                    title={t('low') || 'Faible'}
                                    onMouseEnter={() => setHoveredCertainty({ option: index, level: 'low' })}
                                    onMouseLeave={() => setHoveredCertainty({})}
                                />
                                <button
                                    onClick={(e) => { e.stopPropagation(); handleOptionSelect(option, 'medium'); }}
                                    disabled={loading}
                                    className={`flex-1 h-2 bg-gray-600 rounded-full transition-all duration-200 hover:bg-yellow-500 ${stepData ? (funnelUserChoices[option.answer]?.choice === 'OK' && funnelUserChoices[option.answer]?.confidence === 'medium' ? 'bg-yellow-500' : '') : (selectedOption === option.answer && degreeOfCertainty === 'medium' ? 'bg-yellow-500' : '')} disabled:opacity-50 disabled:cursor-not-allowed`}
                                    title={t('medium') || 'Moyen'}
                                    onMouseEnter={() => setHoveredCertainty({ option: index, level: 'medium' })}
                                    onMouseLeave={() => setHoveredCertainty({})}
                                />
                                <button
                                    onClick={(e) => { e.stopPropagation(); handleOptionSelect(option, 'high'); }}
                                    disabled={loading}
                                    className={`flex-1 h-2 bg-gray-600 rounded-full transition-all duration-200 hover:bg-green-500 ${stepData ? (funnelUserChoices[option.answer]?.choice === 'OK' && funnelUserChoices[option.answer]?.confidence === 'high' ? 'bg-green-500' : '') : (selectedOption === option.answer && degreeOfCertainty === 'high' ? 'bg-green-500' : '')} disabled:opacity-50 disabled:cursor-not-allowed`}
                                    title={t('high') || 'Élevé'}
                                    onMouseEnter={() => setHoveredCertainty({ option: index, level: 'high' })}
                                    onMouseLeave={() => setHoveredCertainty({})}
                                />
                            </div>
                            {/* KO row */}
                            <div className="flex items-center space-x-2">
                                <span className="text-xs text-red-300 min-w-[28px]">KO</span>
                                <button
                                    onClick={(e) => { e.stopPropagation(); setSelectedAnswers(prev => { const n = new Set(prev); n.delete(option.answer); return n; }); setMarkedKOAnswers(prev => { const n = new Set(prev); n.add(option.answer); return n; }); setFunnelUserChoices(prev => ({ ...prev, [option.answer]: { choice: 'KO', confidence: 'low' } })); setTimeout(() => checkCompletion(), 0); }}
                                    disabled={loading}
                                    className={`flex-1 h-2 bg-gray-600 rounded-full transition-all duration-200 hover:bg-red-500 ${funnelUserChoices[option.answer]?.choice === 'KO' && funnelUserChoices[option.answer]?.confidence === 'low' ? 'bg-red-500' : ''} disabled:opacity-50 disabled:cursor-not-allowed`}
                                    title={t('low') || 'Faible'}
                                />
                                <button
                                    onClick={(e) => { e.stopPropagation(); setSelectedAnswers(prev => { const n = new Set(prev); n.delete(option.answer); return n; }); setMarkedKOAnswers(prev => { const n = new Set(prev); n.add(option.answer); return n; }); setFunnelUserChoices(prev => ({ ...prev, [option.answer]: { choice: 'KO', confidence: 'medium' } })); setTimeout(() => checkCompletion(), 0); }}
                                    disabled={loading}
                                    className={`flex-1 h-2 bg-gray-600 rounded-full transition-all duration-200 hover:bg-yellow-500 ${funnelUserChoices[option.answer]?.choice === 'KO' && funnelUserChoices[option.answer]?.confidence === 'medium' ? 'bg-yellow-500' : ''} disabled:opacity-50 disabled:cursor-not-allowed`}
                                    title={t('medium') || 'Moyen'}
                                />
                                <button
                                    onClick={(e) => { e.stopPropagation(); setSelectedAnswers(prev => { const n = new Set(prev); n.delete(option.answer); return n; }); setMarkedKOAnswers(prev => { const n = new Set(prev); n.add(option.answer); return n; }); setFunnelUserChoices(prev => ({ ...prev, [option.answer]: { choice: 'KO', confidence: 'high' } })); setTimeout(() => checkCompletion(), 0); }}
                                    disabled={loading}
                                    className={`flex-1 h-2 bg-gray-600 rounded-full transition-all duration-200 hover:bg-green-500 ${funnelUserChoices[option.answer]?.choice === 'KO' && funnelUserChoices[option.answer]?.confidence === 'high' ? 'bg-green-500' : ''} disabled:opacity-50 disabled:cursor-not-allowed`}
                                    title={t('high') || 'Élevé'}
                                />
                            </div>
                        </div>

                        </div>
                    );
                })}
            </div>

            {/* Loading State */}
            {loading && (
                <div className="text-center py-4">
                    <div className="inline-flex items-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-400" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="text-blue-400">{t('processing_answer')}</span>
                    </div>
                </div>
            )}

        </div>
    );
};

export default QCMOptions;